import { useMemo } from "react";

import { PRO014_STORE_MODE } from "@/@types/campaign";
import type { IPro014StoreWindow, Pro014StoreScope } from "@/@types/campaign";
import { useGetStoresFilterableOptionsQuery } from "@/services/store/select-options";
import { formatDateToDMY } from "@/utils/format-date-ddmmyyyy";

import { sortedStoreWindows } from "../../utils/pro014-stores-diff";
import { PRO014_STORES_TEXTS } from "./constants";

const ACTIVE_WINDOW_CLASS = "bg-background";
const ENDED_WINDOW_CLASS = "bg-muted/50 text-muted-foreground";

interface Pro014StoreWindowsProps {
  detail: Pro014StoreScope;
}

export function Pro014StoreWindows({ detail }: Pro014StoreWindowsProps): JSX.Element {
  const { data: storeOptions = [] } = useGetStoresFilterableOptionsQuery();
  const storeNames: Map<number, string> = useMemo(
    () => new Map(storeOptions.map((option) => [Number(option.value), option.label])),
    [storeOptions],
  );
  const windows: IPro014StoreWindow[] = sortedStoreWindows(detail.storeWindows);

  if (detail.storeMode === PRO014_STORE_MODE.ALL) {
    return <p className="text-sm text-muted-foreground">{PRO014_STORES_TEXTS.ALL_STORES_SCOPE}</p>;
  }

  if (windows.length === 0) {
    return <p className="text-sm text-muted-foreground">{PRO014_STORES_TEXTS.NO_STORES}</p>;
  }

  const storeTitle = (storeCode: number): string => {
    const name: string | undefined = storeNames.get(storeCode);
    if (!name) return PRO014_STORES_TEXTS.STORE_LABEL(storeCode);
    return PRO014_STORES_TEXTS.STORE_WITH_NAME(storeCode, name);
  };

  return (
    <ul
      className="grid max-h-60 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2"
      aria-label={PRO014_STORES_TEXTS.TITLE}
    >
      {windows.map((window: IPro014StoreWindow) => (
        <li
          key={window.storeCode}
          className={`min-w-0 rounded-md border p-2 text-xs ${window.active ? ACTIVE_WINDOW_CLASS : ENDED_WINDOW_CLASS}`}
        >
          <p className="break-words font-medium">{storeTitle(window.storeCode)}</p>
          <p>
            {PRO014_STORES_TEXTS.WINDOW_LABEL(
              formatDateToDMY(window.startDate),
              formatDateToDMY(window.endDate),
            )}
          </p>
          {!window.active && (
            <p className="font-medium text-amber-700 dark:text-amber-400">
              {PRO014_STORES_TEXTS.ENDED_AT(formatDateToDMY(window.endDate))}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}
